<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'
import { createHourlyDABid, updateHourlyDABid } from '@/services/eos'
import type { IHourlyDABidForm } from '@/types/eos/forms'
import type { ISiteHourlyDABid } from '@/types/eos/Table'
import { type FormInstance } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, reactive, ref, useTemplateRef } from 'vue'

const open = defineModel<boolean>()

const props = defineProps<{
  site: ISiteIdentifier
  timezone: string
  currentHourlyBid?: ISiteHourlyDABid
}>()

const emit = defineEmits(['update'])

const isEditMode = ref<boolean>(false)
const hourlyDABidFormRef = useTemplateRef<FormInstance>('hourlyDABidFormRef')
const hourlyDABidForm = reactive<IHourlyDABidForm>({
  for_hour_utc: '',
  price_usd: undefined,
  load_mw: undefined,
  pr_strike_price_usd: undefined,
  pr_load_mw: undefined,
  energy_market_non_curtailable_load_mw: undefined,
  ancillary_services_market_non_curtailable_load_mw: undefined,
  site_id: '',
})

const hourlyDABidAddLoading = ref<boolean>(false)

const editMode = computed(() => (isEditMode.value ? 'Edit' : 'Add'))

const hourlyDABidDialogHourEnding = computed(() => {
  if (!props.currentHourlyBid || !props.timezone) return
  return DateTime.fromISO(props.currentHourlyBid!.for_hour_utc)
    .setZone(props.timezone)
    .plus({ hours: 1 })
    .toFormat('HH:mm ZZZZ')
})

const dialogTitle = computed<string>(() => `${editMode.value} Hourly DA Bid for ${props.site.site_name}`)
const isTzOverride = computed<boolean>(() => props.site.timezone !== props.timezone)

/**
 * Handles dialog open.
 */
const onDialogOpen = () => {
  if (props.currentHourlyBid?.price_usd) {
    isEditMode.value = true
  }

  Object.assign(hourlyDABidForm, {
    for_hour_utc: props.currentHourlyBid?.for_hour_utc,
    price_usd: props.currentHourlyBid?.price_usd,
    load_mw: props.currentHourlyBid?.load_mw,
    pr_strike_price_usd: props.currentHourlyBid?.pr_strike_price_usd,
    pr_load_mw: props.currentHourlyBid?.pr_load_mw,
    energy_market_non_curtailable_load_mw: props.currentHourlyBid?.energy_market_non_curtailable_load_mw,
    ancillary_services_market_non_curtailable_load_mw:
      props.currentHourlyBid?.ancillary_services_market_non_curtailable_load_mw,
    site_id: props.site.site_id,
  })
}

/**
 * Handles dialog submit (creates or updates an hourly DA bid).
 */
const onDialogSubmit = async () => {
  if (!hourlyDABidFormRef.value) return

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  await hourlyDABidFormRef.value.validate(async (valid, fields) => {
    if (!valid) {
      return
    }

    hourlyDABidAddLoading.value = true
    const operation = isEditMode.value ? 'update' : 'create'

    try {
      if (isEditMode.value) {
        await updateHourlyDABid(hourlyDABidForm)
      } else {
        await createHourlyDABid(hourlyDABidForm)
      }

      NotifySuccess(`Successfully ${operation}d hourly DA bid for ${hourlyDABidDialogHourEnding.value}.`)
      emit('update')
    } catch {
      NotifyFail(`Failed to ${operation} hourly DA bid for ${hourlyDABidDialogHourEnding.value}.`)
    } finally {
      open.value = false
      hourlyDABidAddLoading.value = false
    }
  })
}

/**
 * Handles dialog closed.
 */
const onDialogClosed = () => {
  isEditMode.value = false
  hourlyDABidFormRef.value?.resetFields()
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="dialogTitle"
    :show-close="false"
    @open="onDialogOpen"
    @closed="onDialogClosed"
  >
    <el-form ref="hourlyDABidFormRef" :model="hourlyDABidForm" label-width="auto" label-position="left">
      <el-form-item :label="`Hour (${timezone})`">
        <template #label>
          Hour Ending
          <span :class="isTzOverride ? 'text-xanthous-500' : 'text-text-tertiary'"
            >&nbsp;<el-tooltip :disabled="!isTzOverride" type="warning" :content="`Site's timezone: ${site.timezone}`"
              >({{ timezone }})</el-tooltip
            ></span
          >
        </template>
        <el-input readonly disabled :input-style="{ 'text-align': 'center' }" :value="hourlyDABidDialogHourEnding" />
      </el-form-item>
      <el-form-item label="Position">
        <div class="inline-flex gap-4">
          <el-form-item prop="load_mw">
            <el-input-number
              v-model="hourlyDABidForm.load_mw"
              :precision="3"
              :min="0.0"
              class="w-full"
              controls-position="right"
            >
              <template #suffix>MW</template>
            </el-input-number>
          </el-form-item>
          <el-form-item prop="price_usd">
            <el-input-number
              v-model="hourlyDABidForm.price_usd"
              class="w-full"
              :precision="2"
              :min="0.0"
              controls-position="right"
            >
              <template #prefix>$</template>
            </el-input-number>
          </el-form-item>
        </div>
      </el-form-item>
      <el-form-item label="PR Strike Position">
        <div class="inline-flex gap-4">
          <el-form-item prop="pr_load_mw">
            <el-input-number
              v-model="hourlyDABidForm.pr_load_mw"
              :precision="3"
              :min="0.0"
              class="w-full"
              controls-position="right"
            >
              <template #suffix>MW</template>
            </el-input-number>
          </el-form-item>
          <el-form-item prop="pr_strike_price_usd">
            <el-input-number
              v-model="hourlyDABidForm.pr_strike_price_usd"
              class="w-full"
              :precision="2"
              :min="0.0"
              controls-position="right"
            >
              <template #prefix>$</template>
            </el-input-number>
          </el-form-item>
        </div>
      </el-form-item>
      <el-form-item label="Energy Market Non-Curtailable Load" prop="energy_market_non_curtailable_load_mw">
        <el-input-number
          v-model="hourlyDABidForm.energy_market_non_curtailable_load_mw"
          class="w-full"
          :precision="3"
          :min="0.0"
          controls-position="right"
        >
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
      <el-form-item label="ASM Non-Curtailable Load" prop="ancillary_services_market_non_curtailable_load_mw">
        <el-input-number
          v-model="hourlyDABidForm.ancillary_services_market_non_curtailable_load_mw"
          class="w-full"
          :precision="3"
          :min="0.0"
          controls-position="right"
        >
          <template #suffix>MW</template>
        </el-input-number>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="hourlyDABidAddLoading" @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="hourlyDABidAddLoading" @click="onDialogSubmit">Confirm</el-button>
    </template>
  </el-dialog>
</template>
